import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, setAuthToken } from "../lib/api";

/**
 * Public page for one-time admin invite links:
 *   /admin/accept-invite?token=...
 * New users set name + password; existing users are promoted in place and
 * sent to login (their password is never touched).
 */
export default function AdminAcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking|invalid|form|promoted
  const [email, setEmail] = useState("");
  const [existing, setExisting] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/admin-invite/${token}`)
      .then(({ data }) => {
        setEmail(data.email);
        setExisting(data.existing_user);
        setState("form");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const accept = async (e) => {
    e?.preventDefault();
    if (!existing) {
      if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
      if (password !== confirm) { toast.error("Passwords don't match."); return; }
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/admin-invite/${token}/accept`,
        existing ? {} : { name, password });
      if (data.promoted) {
        setState("promoted");
      } else {
        setAuthToken(data.token);
        toast.success("Welcome to the Clinq admin team!");
        navigate("/admin/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't accept the invite.");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-950 p-4">
      <div className="w-full max-w-md rounded-2xl border border-neutral-800 bg-neutral-900 p-8" data-testid="accept-invite-card">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Clinq admin</p>

        {state === "checking" && (
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-neutral-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your invite…
          </p>
        )}

        {state === "invalid" && (
          <div className="mt-4" data-testid="accept-invite-invalid">
            <AlertTriangle className="h-8 w-8 text-amber-400" />
            <h1 className="mt-3 text-xl font-extrabold text-neutral-100">This invite link isn't valid</h1>
            <p className="mt-2 text-sm leading-relaxed text-neutral-400">
              It may have expired, been revoked, or already been used.
              Ask the admin who invited you to send a fresh one.
            </p>
            <Link to="/login" className="mt-5 inline-block text-sm font-semibold text-[#F59E0B] hover:underline">Go to login →</Link>
          </div>
        )}

        {state === "promoted" && (
          <div className="mt-4" data-testid="accept-invite-promoted">
            <ShieldCheck className="h-8 w-8 text-emerald-400" />
            <h1 className="mt-3 text-xl font-extrabold text-neutral-100">You're an admin now</h1>
            <p className="mt-2 text-sm leading-relaxed text-neutral-400">
              Your existing account <b className="text-neutral-200">{email}</b> has been upgraded to admin.
              Log in with your usual password.
            </p>
            <Link to="/login" data-testid="accept-invite-login"
              className="mt-5 inline-block rounded-full bg-[#F59E0B] px-5 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308]">
              Log in
            </Link>
          </div>
        )}

        {state === "form" && (
          <form onSubmit={accept} className="mt-4" data-testid="accept-invite-form">
            <h1 className="text-xl font-extrabold text-neutral-100">
              {existing ? "Accept your admin invitation" : "Set up your admin account"}
            </h1>
            <p className="mt-1 text-sm text-neutral-400">
              Invitation for <b className="text-neutral-200">{email}</b>
            </p>
            {!existing && (
              <>
                <label className="mt-4 block text-xs font-semibold text-neutral-300" htmlFor="inv-name">Your name</label>
                <input id="inv-name" required value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="accept-invite-name"
                  className="mt-1 w-full rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 focus:border-[#F59E0B] focus:outline-none" />
                <label className="mt-3 block text-xs font-semibold text-neutral-300" htmlFor="inv-pass">Password (min 8 characters)</label>
                <input id="inv-pass" type="password" required minLength={8} value={password}
                  data-testid="accept-invite-password"
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 focus:border-[#F59E0B] focus:outline-none" />
                <label className="mt-3 block text-xs font-semibold text-neutral-300" htmlFor="inv-confirm">Confirm password</label>
                <input id="inv-confirm" type="password" required value={confirm}
                  data-testid="accept-invite-confirm"
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 focus:border-[#F59E0B] focus:outline-none" />
              </>
            )}
            {existing && (
              <p className="mt-3 rounded-lg border border-sky-500/30 bg-sky-500/10 p-3 text-xs leading-relaxed text-sky-300">
                You already have a Clinq account with this email — accepting will upgrade it to
                admin. Your password stays the same.
              </p>
            )}
            <button type="submit" disabled={busy}
              data-testid="accept-invite-submit"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F59E0B] px-5 py-2.5 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {existing ? "Accept invitation" : "Create admin account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
